import styled from "@emotion/styled";
import sidekickLogo from "@assets/sidekick-logo.svg";
import {
  ChatMessage,
  ChatMessageArtifact,
  ChatMessageMetadata,
} from "@store/messages.store";
import { MessageLoading } from "./MessageLoading";
import { BotMessageProps, Message } from "./Message";
import { CHAT_CONTENT_MAX_WIDTH, SIDEKICK_NAME } from "../chat-helpers";
import { CircularProgress, Typography } from "@mui/material";
import { ImageGallery } from "@shared/components/ImageGallery";
import { ProactiveMessageSuggestions } from "./ProactiveMessageSuggestions";
import { appEventEmitter } from "@shared/events/app-event-emitter";
import { Box } from "@avenue-8/sidekick-ui";
import { useInfiniteScrollMessages } from "../hooks/useInfiniteScrollMessages";
import { useEffect, useRef, useState } from "react";
import { useHasModule } from "@shared/access-control/useHasModule";
import { sidekickModuleEnum } from "@shared/access-control/sidekick-modules";

const Container = styled.div<{ $ready?: boolean }>`
  position: relative;
  overflow-y: auto;
  width: 100%;
  overflow-y: ${(p) => (p.$ready ? "auto" : "hidden")};
  visibility: ${(p) => (p.$ready ? "visible" : "hidden")};

  ${(p) => p.theme.media.laptopDown} {
    padding-bottom: 5rem;
  }

  &[aria-disabled="true"] {
    opacity: 0.3;
    pointer-events: none;
  }
`;

const FlexContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 1.5rem;
`;

const MessagesContent = styled.div`
  display: flex;
  flex-direction: column;
  max-width: ${CHAT_CONTENT_MAX_WIDTH};
  padding: 2.5rem 2.25rem 0 2.25rem;
  margin: auto;
  padding-top: 2.5rem;

  ${(p) => p.theme.media.tabletDown} {
    padding: 1.5rem 1.25rem 0 1.25rem;
  }
`;

const MessagesBottomSpacer = styled.div<{ $spacerHeight: string }>`
  overflow: hidden;
  transition:
    height 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  height: ${(p) => p.$spacerHeight};
  max-height: ${(p) => p.$spacerHeight};
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${(p) => p.theme.media.mobileDown} {
    height: initial;
  }
`;

const BlockLoadingWrapper = styled(LoadingWrapper)`
  align-items: center;
  width: 100%;
  padding: 10rem 0;
  color: ${(p) => p.theme.text.colors.primary};

  ${(p) => p.theme.media.mobileDown} {
    padding: 6rem 0;
  }
`;

interface Props {
  isSendingMessage: boolean;
  isReconnecting: boolean;
  isReceivingMessageResponse: boolean;
  currentThread: string | undefined;
  messages: ChatMessage[];
  isLoadingMessages: boolean;
  getMessageMetadata: (
    threadId: string,
    messageId: string,
  ) => ChatMessageMetadata | undefined;
  handleThreadSuggestionClick: (suggestion: { prompt: string }) => void;
  handleArtifactClick: (artifact: ChatMessageArtifact) => void;
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  isFetchingNextPage?: boolean;
  messagesContainerRef: React.RefObject<HTMLDivElement>;
  isReady: boolean;
  hasMoreAutoInteractions?: boolean;
}

export const Messages = (props: Props) => {
  const {
    isSendingMessage,
    isReconnecting,
    isReceivingMessageResponse,
    messages,
    getMessageMetadata,
    isLoadingMessages,
    currentThread,
    handleThreadSuggestionClick,
    handleArtifactClick,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    messagesContainerRef,
    isReady,
    hasMoreAutoInteractions = false,
  } = props;

  // Keyed by message index (stable across temp→server ID swap on stream sync)
  const timelineOpenStates = useRef<Map<number, boolean>>(new Map());

  const SCROLL_TO_BOTTOM_THRESHOLD = 1000;
  const lastUserMessageRef = useRef<HTMLDivElement | null>(null);
  const spacerRef = useRef<HTMLDivElement | null>(null);
  const [isUserAtBottom, setIsUserAtBottom] = useState(true);
  const hasMessageBeingPrepared = messages.some((m) => !!m.streaming);
  const hasMlsAccessModule = useHasModule(sidekickModuleEnum.MLS_ACCESS);

  useInfiniteScrollMessages({
    messagesContainerRef,
    messages,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    threshold: 100,
  });

  // Scroll to last user message when new messages are added
  useEffect(() => {
    setTimeout(() => {
      if (lastUserMessageRef.current) {
        lastUserMessageRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 200);
  }, [messages.length]);

  const shouldReserveScrollSpace =
    isReceivingMessageResponse || isSendingMessage || hasMessageBeingPrepared;

  const spacerHeight =
    isUserAtBottom && !shouldReserveScrollSpace ? "0" : "40dvh";

  // Track if user is scrolled to bottom
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !isReady) return;

    let debounceTimeout: NodeJS.Timeout;

    const handleScroll = () => {
      clearTimeout(debounceTimeout);

      debounceTimeout = setTimeout(() => {
        const isAtBottom =
          container.scrollHeight -
            container.scrollTop -
            container.clientHeight <
          SCROLL_TO_BOTTOM_THRESHOLD;

        setIsUserAtBottom(isAtBottom);
      }, 200);
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      clearTimeout(debounceTimeout);
      container.removeEventListener("scroll", handleScroll);
    };
  }, [isReady, messagesContainerRef]);

  const handleShowSuggestionsClick = () => {
    appEventEmitter.emit({
      eventType: "sidekick-suggestions-show-click",
    });
  };

  const handleHideSuggestionsClick = () => {
    appEventEmitter.emit({
      eventType: "sidekick-suggestions-hide-click",
    });
  };

  const handleRefreshSuggestionsClick = () => {
    appEventEmitter.emit({
      eventType: "sidekick-suggestions-refresh-click",
    });
  };

  const mapChatSubMessageToSubMessage = (
    source: ChatMessage,
  ): BotMessageProps => {
    const mappedMessage = {
      id: source.id,
      text: source.text,
      createdBy: "bot",
      authorName: source.user.name,
      authorAvatar: source.user.avatar,
      type: source.type,
      isPreparingMessageResponse: source.streaming,
    } as const;
    return mappedMessage;
  };

  if (isLoadingMessages && !isSendingMessage) {
    return (
      <Container $ready={true}>
        {isLoadingMessages && !isSendingMessage && (
          <FlexContainer>
            <BlockLoadingWrapper>
              <CircularProgress />
            </BlockLoadingWrapper>
          </FlexContainer>
        )}
      </Container>
    );
  }

  return (
    <Container ref={messagesContainerRef} $ready={isReady}>
      <MessagesContent data-role="messages-content">
        {isFetchingNextPage && (
          <Box display="flex" justifyContent="center" mb={4}>
            <Typography>
              <CircularProgress size={20} />
            </Typography>
          </Box>
        )}
        {messages.map((message, index) =>
          message.createdBy === "user" ? (
            <Message
              key={message.id}
              id={message.id}
              text={message.text}
              authorName={message.user.name}
              authorAvatar={message.user.avatar}
              type={message.type}
              createdBy={"user"}
              ref={
                index === messages.length - 1 ? lastUserMessageRef : undefined
              }
            />
          ) : (
            <Message
              key={message.id}
              id={message.id}
              text={message.text}
              authorName={message.user.name}
              authorAvatar={message.user.avatar}
              type={message.type}
              subMessages={message.subMessages?.map(
                mapChatSubMessageToSubMessage,
              )}
              artifacts={message.artifacts}
              createdBy={"bot"}
              isPreparingMessageResponse={message.streaming}
              _metadata={getMessageMetadata(currentThread!, message.id)}
              onArtifactClick={handleArtifactClick}
              defaultTimelineOpen={timelineOpenStates.current.get(index)}
              onTimelineOpenChange={(isOpen) =>
                timelineOpenStates.current.set(index, isOpen)
              }
            />
          ),
        )}

        {isSendingMessage && !hasMessageBeingPrepared && (
          <Message
            id={"loading-message"}
            authorName={SIDEKICK_NAME}
            authorAvatar={sidekickLogo}
            type="text"
            createdBy={"bot"}
            isPreparingMessageResponse={true}
          />
        )}
        {isReconnecting && (
          <Message
            id={"reconnecting-message"}
            customContent={
              <LoadingWrapper>
                <MessageLoading
                  key="reconnecting-message"
                  messages={["Connection lost. Reconnecting..."]}
                />
              </LoadingWrapper>
            }
            authorName={SIDEKICK_NAME}
            authorAvatar={sidekickLogo}
            type="text"
            createdBy={"bot"}
          />
        )}
        {hasMlsAccessModule && (
          <ProactiveMessageSuggestions
            threadId={currentThread}
            isBlocked={
              isSendingMessage ||
              isReceivingMessageResponse ||
              isLoadingMessages ||
              hasMoreAutoInteractions
            }
            // onThreadSuggestionGenerated={handleScrollToLastMessage}
            onShowSuggestionsClick={handleShowSuggestionsClick}
            onHideSuggestionsClick={handleHideSuggestionsClick}
            onRefreshSuggestionsClick={handleRefreshSuggestionsClick}
            onThreadSuggestionClick={handleThreadSuggestionClick}
          />
        )}

        <MessagesBottomSpacer
          ref={spacerRef}
          aria-description="messages-bottom"
          $spacerHeight={spacerHeight}
        />
      </MessagesContent>
      <ImageGallery />
    </Container>
  );
};

export const MessagesSkeleton = () => {
  return (
    <Container $ready={true}>
      <FlexContainer>
        <BlockLoadingWrapper>
          <CircularProgress />
        </BlockLoadingWrapper>
      </FlexContainer>
    </Container>
  );
};
